import React from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose } from "react-icons/io5";
import { FiCheck, FiEdit3, FiEye, FiEyeOff, FiRefreshCw, FiTrash2, FiUploadCloud } from "react-icons/fi";
import { invalidate } from '@react-three/fiber';
import { observer } from 'mobx-react-lite';
import { editorStore } from '../../store/editorStore';
import { floorplannerStore } from '../../store/floorplannerStore';
import { BackdropImageType, isBackdropImageType } from '../../types/floorplannerTypes';
import { isBackdropVisible } from '../../utils/backdropImage';
import { DeleteFloorplanBackdropImageDocument } from '../../gql/floorplanBackdrop';
import { useFloorplansQuery } from '../../gql';
import {
    FloorplanImageImportStatus,
    useCancelFloorplanImageImportMutation,
    useFloorplanImageImportsQuery,
} from '../../gql/graphql';

interface DropdownProps {
    position: { top: number; left: number };
    onClose: () => void;
    dropdownRef: React.Ref<HTMLDivElement>;
    onAddBackgroundImage: () => void;
    onCreateEditableDrawing: () => void;
    onOpenImageImport: (id: string) => void;
}

const PANEL_WIDTH = 398;
/** Keep at least this much of the panel on-screen; also used to push tall lists upward. */
const PANEL_MIN_VISIBLE_HEIGHT = 280;
const PANEL_VIEWPORT_GAP = 12;
/** Prefer up to this fraction of the viewport when shifting the panel upward. */
const PANEL_PREFERRED_HEIGHT_RATIO = 0.7;

const getViewportSize = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
});

export const UploadsPanel: React.FC<DropdownProps> = observer(({ position, onClose, dropdownRef, onAddBackgroundImage, onCreateEditableDrawing, onOpenImageImport }) => {
    const { t } = useTranslation();
    const [showAppliedImports, setShowAppliedImports] = React.useState(false);
    const [viewportSize, setViewportSize] = React.useState(getViewportSize);

    React.useEffect(() => {
        const updateViewportSize = () => setViewportSize(getViewportSize());
        window.addEventListener('resize', updateViewportSize);
        return () => window.removeEventListener('resize', updateViewportSize);
    }, []);
    const backdrops = Array.from(floorplannerStore.symbolsMap.values()).filter(isBackdropImageType);
    const floorplansQuery = useFloorplansQuery({ variables: {} });
    const { data } = floorplansQuery;
    // List all of the current user's import jobs (not scoped to this floorplan).
    const importsQuery = useFloorplanImageImportsQuery({
        variables: {
            activeOnly: true,
            limit: 25,
        },
        fetchPolicy: "cache-and-network",
        pollInterval: 5000,
    });
    const [cancelImport] = useCancelFloorplanImageImportMutation();
    const imageImports = importsQuery.data?.floorplanImageImports ?? [];
    const importsLoading =
        importsQuery.loading && !importsQuery.data?.floorplanImageImports;
    const floorplansLoading = floorplansQuery.loading && !floorplansQuery.data?.floorplans;
    const activeImageImports = imageImports.filter(
        (imageImport) => imageImport.status !== FloorplanImageImportStatus.Applied,
    );
    const appliedImageImports = imageImports.filter(
        (imageImport) => imageImport.status === FloorplanImageImportStatus.Applied,
    );
    const currentUrls = new Set(backdrops.map((backdrop) => backdrop.backdropImagePath));
    const savedBackdrops = (data?.floorplans ?? [])
        .flatMap((floorplan) => {
            if (!floorplan) return [];
            const parsed: Array<{
                id: string;
                floorplanName: string;
                imageUrl: string;
                backdropRatio: number;
                backdropSize: number;
            }> = [];

            if (floorplan.backdropImage) {
                parsed.push({
                    id: `${floorplan.id}-root-${floorplan.backdropImage}`,
                    floorplanName: floorplan.name || t('common.untitled'),
                    imageUrl: floorplan.backdropImage,
                    backdropRatio: floorplan.backdropRatio ?? 1,
                    backdropSize: floorplan.backdropSize ?? 1000,
                });
            }

            try {
                const json = floorplan.json ? JSON.parse(floorplan.json) : null;
                const symbols = Array.isArray(json?.symbols) ? json.symbols : [];
                symbols.forEach((symbol: any) => {
                    if (symbol?.type === "backdropImage" && symbol.backdropImagePath) {
                        parsed.push({
                            id: `${floorplan.id}-${symbol.id}`,
                            floorplanName: floorplan.name || t('common.untitled'),
                            imageUrl: symbol.backdropImagePath,
                            backdropRatio: symbol.backdropRatio ?? 1,
                            backdropSize: symbol.backdropSize ?? 1000,
                        });
                    }
                });
            } catch {
                return parsed;
            }

            return parsed;
        })
        .filter((item, index, all) =>
            !currentUrls.has(item.imageUrl) &&
            all.findIndex((candidate) => candidate.imageUrl === item.imageUrl) === index
        );

    const selectBackdrop = (backdrop: BackdropImageType) => {
        editorStore.clearSelections();
        floorplannerStore.updateSymbolProperty(backdrop.id, "selected", true);
        editorStore.addSelection(backdrop);
        invalidate();
        onClose();
    };

    const toggleBackdropVisibility = (event: React.MouseEvent, backdrop: BackdropImageType) => {
        event.stopPropagation();
        if (floorplannerStore.lockedByOther) return;
        floorplannerStore.beginUndoTransaction('backdropVisibility');
        floorplannerStore.recordSymbolForUndo(backdrop.id);
        floorplannerStore.updateSymbolProperty(backdrop.id, "visible", !isBackdropVisible(backdrop));
        floorplannerStore.recordSymbolChange(backdrop.id, 'modify');
        floorplannerStore.pushToUndoStack();
        floorplannerStore.setDirty();
        invalidate();
    };

    const deleteBackdrop = async (event: React.MouseEvent, backdrop: BackdropImageType) => {
        event.stopPropagation();
        if (floorplannerStore.lockedByOther) return;
        const imageUrl = backdrop.backdropImagePath;
        const floorplanId = floorplannerStore.id;
        editorStore.removeSelection(backdrop);
        floorplannerStore.removeSymbol(backdrop.id);
        invalidate();

        if (!floorplanId || !imageUrl) return;

        try {
            await (floorplannerStore.client ?? editorStore.client)?.mutate({
                mutation: DeleteFloorplanBackdropImageDocument,
                variables: { floorplanId, imageUrl },
            });
        } catch (error) {
            console.warn("Could not delete floorplan backdrop image", error);
        }
    };

    const insertSavedBackdrop = (backdrop: typeof savedBackdrops[number]) => {
        const [centerX, centerY] = editorStore.getVisibleCenter();
        const id = floorplannerStore.addSymbol("backdropImage", [centerX, centerY], {
            backdropImagePath: backdrop.imageUrl,
            backdropRatio: backdrop.backdropRatio,
            backdropSize: backdrop.backdropSize,
            locked: false,
            zIndex: -1,
        });
        const symbol = floorplannerStore.symbolsMap.get(id);
        if (symbol) {
            editorStore.clearSelections();
            floorplannerStore.updateSymbolProperty(id, "selected", true);
            editorStore.addSelection(symbol);
        }
        invalidate();
        onClose();
    };

    const discardImageImport = async (
        event: React.MouseEvent,
        importId: string,
    ) => {
        event.stopPropagation();
        try {
            await cancelImport({ variables: { id: importId } });
            await importsQuery.refetch();
        } catch (error) {
            console.warn("Could not discard floorplan image import", error);
        }
    };

    // Keep the panel inside the viewport (same idea as UserModels3DPanel).
    // A fixed 70vh max-height ignores `top`, so a low-anchored panel was clipped
    // by the window bottom and the end of the list could not be reached.
    const availableViewportHeight = Math.max(
        0,
        viewportSize.height - PANEL_VIEWPORT_GAP * 2,
    );
    const preferredMaxHeight = Math.min(
        availableViewportHeight,
        Math.round(viewportSize.height * PANEL_PREFERRED_HEIGHT_RATIO),
    );
    const minimumVisibleHeight = Math.min(
        Math.max(PANEL_MIN_VISIBLE_HEIGHT, preferredMaxHeight),
        availableViewportHeight,
    );
    const panelTop = Math.min(
        Math.max(PANEL_VIEWPORT_GAP, position.top),
        Math.max(
            PANEL_VIEWPORT_GAP,
            viewportSize.height - PANEL_VIEWPORT_GAP - minimumVisibleHeight,
        ),
    );
    const panelWidth = Math.max(
        0,
        Math.min(PANEL_WIDTH, viewportSize.width - PANEL_VIEWPORT_GAP * 2),
    );
    const panelLeft = Math.min(
        Math.max(PANEL_VIEWPORT_GAP, position.left),
        Math.max(
            PANEL_VIEWPORT_GAP,
            viewportSize.width - PANEL_VIEWPORT_GAP - panelWidth,
        ),
    );
    const panelMaxHeight = Math.max(
        0,
        viewportSize.height - panelTop - PANEL_VIEWPORT_GAP,
    );

    return (
        <div
            className="fixed z-50 min-h-40 overflow-y-auto rounded border border-gray-300 bg-white shadow-md scrollbar"
            style={{
                top: panelTop,
                left: panelLeft,
                width: panelWidth,
                maxHeight: panelMaxHeight,
            }}
            ref={dropdownRef}
            onClick={(event) => event.stopPropagation()}
        >
            <div className="flex flex-cols items-center justify-between p-2">
                <div className="text-sm font-normal">{t('panels.uploads')}</div>
                <div onClick={onClose} className="text-sm text-gray-500"><IoClose style={{ strokeWidth: 0.5 }} size={24} /></div>
            </div>

            {/* Divider line */}
            <div className="border-b border-gray-300 w-full" />
            <div className={`p-3 space-y-3${floorplannerStore.lockedByOther ? ' opacity-50' : ''}`}>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        className="flex min-h-[58px] items-center gap-2 rounded border border-[#edebe6] bg-[#f7f6f0] p-2.5 text-left hover:bg-[#edebe6] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-not-allowed disabled:hover:bg-[#f7f6f0]"
                        onClick={onCreateEditableDrawing}
                        disabled={floorplannerStore.lockedByOther}
                    >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white">
                            <FiEdit3 size={17} />
                        </span>
                        <span className="min-w-0">
                            <span className="block text-sm font-medium leading-4 text-black">{t('uploads.createEditableDrawing')}</span>
                            <span className="block text-[11px] leading-4 text-gray-600">{t('uploads.scanWithAI')}</span>
                        </span>
                    </button>

                    <button
                        type="button"
                        className="flex min-h-[58px] items-center gap-2 rounded border border-[#edebe6] bg-[#f7f6f0] p-2.5 text-left hover:bg-[#edebe6] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-not-allowed disabled:hover:bg-[#f7f6f0]"
                        onClick={onAddBackgroundImage}
                        disabled={floorplannerStore.lockedByOther}
                    >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white">
                            <FiUploadCloud size={17} />
                        </span>
                        <span className="min-w-0">
                            <span className="block text-sm font-medium leading-4 text-black">{t('uploads.addBackgroundImage')}</span>
                            <span className="block text-[11px] leading-4 text-gray-500">{t('uploads.uploadAndSetRealScale')}</span>
                        </span>
                    </button>
                </div>

                {(importsLoading ||
                    activeImageImports.length > 0 ||
                    appliedImageImports.length > 0) && (
                    <div>
                        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">{t('uploads.importJobs')}</div>
                        {importsLoading ? (
                            <div
                                className="flex items-center gap-2 rounded border border-[#edebe6] bg-[#f7f6f0] px-3 py-2.5 text-sm text-gray-600"
                                aria-live="polite"
                            >
                                <FiRefreshCw
                                    size={14}
                                    className="shrink-0 animate-spin text-gray-500 motion-reduce:animate-none"
                                />
                                <span>{t('uploads.loadingImportJobs')}</span>
                            </div>
                        ) : (
                        <div className="max-h-[148px] space-y-2 overflow-y-auto pr-0.5">
                            {activeImageImports.map((imageImport) => {
                                const ready = imageImport.status === FloorplanImageImportStatus.Ready;
                                const failed = imageImport.status === FloorplanImageImportStatus.Failed;
                                const canDiscard = ready || failed;
                                return (
                                    <div
                                        key={imageImport.id}
                                        className="flex w-full items-center gap-3 rounded border border-[#edebe6] bg-white p-2 text-left hover:border-boniYellowPrimary"
                                    >     
                                          <button
                                            type="button"
                                            onClick={() => onOpenImageImport(imageImport.id)}
                                            disabled={floorplannerStore.lockedByOther}
                                            className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-not-allowed"
                                        >
                                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded ${ready ? "bg-emerald-100 text-emerald-700" : failed ? "bg-red-100 text-red-700" : "bg-[#fff4ba] text-[#8a6900]"}`}>
                                                {ready ? <FiCheck size={17} /> : <FiRefreshCw size={16} className={failed ? "" : "animate-spin motion-reduce:animate-none"} />}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium">{imageImport.name}</span>
                                                <span className="block text-xs text-gray-500">
                                                    {ready
                                                          ? t('uploads.readyToReview')
                                                          : failed
                                                            ? t('uploads.needsAttention')
                                                            : `${Math.round(imageImport.progress * 100)}% · ${imageImport.stage.toLowerCase().replaceAll("_", " ")}`}
                                                </span>
                                            </span>
                                        </button>
                                        {canDiscard && (
                                            <button
                                                type="button"
                                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                                onClick={(event) => discardImageImport(event, imageImport.id)}
                                                disabled={floorplannerStore.lockedByOther}
                                                title={t('uploads.discardScan')}
                                                aria-label={t('uploads.discardScan')}
                                            >
                                                <FiTrash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {showAppliedImports &&
                                appliedImageImports.map((imageImport) => (
                                    <div
                                        key={imageImport.id}
                                        className="flex w-full items-center gap-3 rounded border border-[#edebe6] bg-white p-2 text-left hover:border-boniYellowPrimary"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => onOpenImageImport(imageImport.id)}
                                            disabled={floorplannerStore.lockedByOther}
                                            className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-not-allowed"
                                        >
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-emerald-100 text-emerald-700">
                                                <FiCheck size={17} />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium">{imageImport.name}</span>
                                                <span className="block text-xs text-gray-500">{t('uploads.inserted')}</span>
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                            onClick={(event) => discardImageImport(event, imageImport.id)}
                                            disabled={floorplannerStore.lockedByOther}
                                            title={t('uploads.discardScan')}
                                            aria-label={t('uploads.discardScan')}
                                        >
                                            <FiTrash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                        </div>
                        )}
                        {!importsLoading && appliedImageImports.length > 0 && (
                            <button
                                type="button"
                                className="mt-2 text-xs font-medium text-gray-600 underline underline-offset-2 hover:text-black"
                                onClick={() => setShowAppliedImports((current) => !current)}
                            >
                                {showAppliedImports
                                    ? t('uploads.hideInsertedJobs')
                                    : t('uploads.showInsertedJobs', { count: appliedImageImports.length })}
                            </button>
                        )}
                    </div>
                )}

                {backdrops.length > 0 && (
                    <div>
                        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">{t('uploads.inThisFloorplan')}</div>
                        <p className="mb-2 text-[11px] leading-4 text-gray-500">{t('uploads.backgroundImagesHint')}</p>
                        <div className="grid grid-cols-3 gap-2">
                            {backdrops.map((backdrop, index) => {
                                const visible = isBackdropVisible(backdrop);
                                const statusLabel = !visible
                                    ? t('common.hidden')
                                    : backdrop.locked
                                      ? t('common.locked')
                                      : t('common.unlocked');
                                return (
                                <button
                                    key={backdrop.id}
                                    type="button"
                                    className={`group relative h-[86px] overflow-hidden rounded border bg-white text-left ${backdrop.selected ? "border-boniYellowPrimary ring-1 ring-boniYellowPrimary" : "border-[#edebe6]"}`}
                                    onClick={() => selectBackdrop(backdrop)}
                                    title={t('uploads.openBackgroundSettings', { index: index + 1 })}
                                >
                                    <img
                                        src={backdrop.backdropImagePath}
                                        alt={t('uploads.backgroundIndex', { index: index + 1 })}
                                        className={`h-full w-full object-cover ${visible ? '' : 'opacity-40'}`}
                                        crossOrigin="anonymous"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-[11px] text-white">
                                        {statusLabel}
                                    </div>
                                    {!floorplannerStore.lockedByOther && (
                                        <>
                                            <span
                                                className="absolute left-1 top-1 flex h-7 w-7 items-center justify-center rounded bg-white text-gray-700 shadow hover:bg-gray-50"
                                                onClick={(event) => toggleBackdropVisibility(event, backdrop)}
                                                title={visible ? t('uploads.hideBackgroundImage') : t('uploads.showBackgroundImage')}
                                                aria-label={visible ? t('uploads.hideBackgroundImage') : t('uploads.showBackgroundImage')}
                                            >
                                                {visible ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                                            </span>
                                            <span
                                                className="absolute right-1 top-1 hidden h-7 w-7 items-center justify-center rounded bg-white text-red-600 shadow group-hover:flex"
                                                onClick={(event) => deleteBackdrop(event, backdrop)}
                                            >
                                                <FiTrash2 size={14} />
                                            </span>
                                        </>
                                    )}
                                </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {(floorplansLoading || savedBackdrops.length > 0) && (
                    <div>
                        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">{t('uploads.previousBackgrounds')}</div>
                        {floorplansLoading ? (
                            <div
                                className="flex items-center gap-2 rounded border border-[#edebe6] bg-[#f7f6f0] px-3 py-2.5 text-sm text-gray-600"
                                aria-live="polite"
                            >
                                <FiRefreshCw
                                    size={14}
                                    className="shrink-0 animate-spin text-gray-500 motion-reduce:animate-none"
                                />
                                <span>{t('uploads.loadingPreviousBackgrounds')}</span>
                            </div>
                        ) : (
                        <div className="grid grid-cols-3 gap-2">
                            {savedBackdrops.map((backdrop) => (
                                <button
                                    key={backdrop.id}
                                    type="button"
                                    className="group relative h-[86px] overflow-hidden rounded border border-[#edebe6] bg-white text-left hover:border-boniYellowPrimary disabled:cursor-not-allowed disabled:hover:border-[#edebe6]"
                                    onClick={() => insertSavedBackdrop(backdrop)}
                                    disabled={floorplannerStore.lockedByOther}
                                    title={t('uploads.useImageFrom', { name: backdrop.floorplanName })}
                                >
                                    <img
                                        src={backdrop.imageUrl}
                                        alt={t('uploads.backgroundFrom', { name: backdrop.floorplanName })}
                                        className="h-full w-full object-cover"
                                        crossOrigin="anonymous"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-2 py-1 text-[11px] text-white">
                                        {backdrop.floorplanName}
                                    </div>
                                </button>
                            ))}
                        </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});
